import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Users, Briefcase, BookOpen, Headphones, Megaphone,
  Boxes, Wrench, BarChart3, Workflow, Search, Settings, GraduationCap,
  RefreshCcw, Puzzle, ShieldCheck, Rocket
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import CountUp from '../components/CountUp';

const zohoApps = [
  { icon: Users, name: 'Zoho CRM', description: 'Sales pipelines, lead scoring and automation that turn prospects into customers.' },
  { icon: Boxes, name: 'Zoho One', description: 'The all-in-one operating system for business — 45+ integrated apps under one license.' },
  { icon: BookOpen, name: 'Zoho Books', description: 'VAT-compliant accounting built for UAE businesses, from invoicing to reporting.' },
  { icon: Briefcase, name: 'Zoho People', description: 'HR management — onboarding, attendance, leave and performance in one place.' },
  { icon: Headphones, name: 'Zoho Desk', description: 'Omnichannel customer support with SLA tracking and self-service portals.' },
  { icon: Megaphone, name: 'Zoho Campaigns', description: 'Email marketing and automation journeys connected directly to your CRM.' },
  { icon: Wrench, name: 'Zoho Creator', description: 'Custom low-code apps tailored to workflows off-the-shelf tools cannot handle.' },
  { icon: BarChart3, name: 'Zoho Analytics', description: 'Unified dashboards and BI reporting across every department.' },
  { icon: Workflow, name: 'Zoho Flow', description: 'Connect Zoho with 800+ third-party apps through automated workflows.' }
];

const services = [
  { icon: Search, title: 'Consulting & Process Audit', description: 'We map your current processes, identify gaps, and design the right Zoho architecture for your business.' },
  { icon: Settings, title: 'Implementation & Setup', description: 'Full deployment — modules, pipelines, workflows, roles and permissions configured to your operations.' },
  { icon: Puzzle, title: 'Customization & Integration', description: 'Custom functions, Deluge scripting, API integrations with telephony, WhatsApp, payment gateways and more.' },
  { icon: RefreshCcw, title: 'Data Migration', description: 'Secure migration from spreadsheets, legacy CRMs or other platforms — with zero data loss.' },
  { icon: GraduationCap, title: 'Training & Adoption', description: 'Role-based training sessions so your team actually uses the system from day one.' },
  { icon: ShieldCheck, title: 'Ongoing Support', description: 'Dedicated support, health checks and continuous optimization as your business scales.' }
];

const steps = [
  { number: '01', title: 'Discovery Call', description: 'We understand your business model, teams and current tools.' },
  { number: '02', title: 'Solution Blueprint', description: 'A detailed Zoho architecture and implementation roadmap with clear milestones.' },
  { number: '03', title: 'Build & Migrate', description: 'Configuration, customization and clean data migration — tested end to end.' },
  { number: '04', title: 'Train & Grow', description: 'Team training, go-live support and continuous optimization for lasting ROI.' }
];

const whyUs = [
  'Dubai-based team that understands GCC business, VAT and compliance requirements',
  'Automation-first mindset — we build systems, not just software installs',
  'End-to-end delivery: consulting, implementation, integration, training and support',
  'Marketing + technology under one roof — your Zoho connects to your growth engine'
];

export default function Zoho() {
  return (
    <div className="bg-white" data-testid="zoho-page">
      {/* Hero */}
      <section className="py-24 relative overflow-hidden" style={{ backgroundColor: 'rgba(17, 170, 182, 0.05)', borderBottom: '1px solid rgba(5, 46, 108, 0.12)' }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <span
            className="inline-block px-5 py-2 rounded-full font-['Inter'] text-sm font-semibold text-[#11AAB6] mb-6"
            style={{ backgroundColor: 'rgba(17, 170, 182, 0.12)', border: '1px solid rgba(17, 170, 182, 0.25)' }}
            data-testid="zoho-hero-badge"
          >
            Zoho Consulting & Implementation
          </span>
          <h1 className="font-['Space_Grotesk'] font-bold text-5xl md:text-6xl text-[#052E6C] mb-6 leading-tight">
            Run your entire business<br />
            on <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#11AAB6] to-[#052E6C]">Zoho</span>.
          </h1>
          <p className="font-['Inter'] text-xl leading-relaxed max-w-3xl mx-auto mb-10" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
            From Zoho CRM to Zoho One, we help businesses across the UAE and GCC implement, customize and scale the world's most complete business suite — with strategy, automation and support at every step.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              asChild
              className="bg-[#11AAB6] hover:bg-[#0E8A94] text-white px-8 py-6 text-lg rounded-full font-['Inter'] font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
              data-testid="zoho-hero-cta"
            >
              <Link to="/contact">
                Start Your Zoho Journey
                <ArrowRight className="ml-2 inline-block group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-2 border-[#052E6C] text-[#052E6C] hover:text-[#052E6C] px-8 py-6 text-lg rounded-full font-['Inter'] font-medium transition-all duration-300 hover:scale-105 bg-transparent hover:bg-transparent"
            >
              <a href="https://booking.impactechdigital.com" target="_blank" rel="noopener noreferrer">
                Book Free Consultation
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="py-12 bg-white" style={{ borderBottom: '1px solid rgba(5, 46, 108, 0.12)' }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { value: '45+', label: 'Zoho apps in one suite' },
            { value: '800+', label: 'Third-party integrations' },
            { value: '100M+', label: 'Users trust Zoho globally' },
            { value: '24/7', label: 'Support from our team' }
          ].map((stat, i) => (
            <div key={i}>
              <CountUp value={stat.value} className="font-['Space_Grotesk'] font-bold text-3xl md:text-4xl text-transparent bg-clip-text bg-gradient-to-r from-[#11AAB6] to-[#052E6C]" />
              <p className="font-['Inter'] text-sm mt-1" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What we deliver */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-['Inter'] text-sm font-semibold uppercase tracking-[0.2em] text-[#11AAB6] mb-2">What We Deliver</p>
            <h2 className="font-['Space_Grotesk'] font-bold text-4xl md:text-5xl text-[#052E6C] mb-4">
              End-to-end Zoho services
            </h2>
            <p className="font-['Inter'] text-lg max-w-2xl mx-auto" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
              We don't just install software. We architect systems around how your business actually works.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <Card key={i} className="group transition-all duration-300 hover:shadow-lg relative overflow-hidden" style={{ border: '1px solid rgba(5, 46, 108, 0.12)' }} data-testid={`zoho-service-card-${i}`}>
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#11AAB6] to-[#052E6C] transform scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-top" />
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 text-[#11AAB6] group-hover:scale-110 transition-transform" style={{ backgroundColor: 'rgba(17, 170, 182, 0.12)' }}>
                    <service.icon size={24} />
                  </div>
                  <h3 className="font-['Space_Grotesk'] font-bold text-xl text-[#052E6C] mb-3">{service.title}</h3>
                  <p className="font-['Inter'] text-sm leading-relaxed" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Zoho apps we implement */}
      <section className="py-24" style={{ backgroundColor: 'rgba(17, 170, 182, 0.05)' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-['Inter'] text-sm font-semibold uppercase tracking-[0.2em] text-[#11AAB6] mb-2">The Zoho Ecosystem</p>
            <h2 className="font-['Space_Grotesk'] font-bold text-4xl md:text-5xl text-[#052E6C] mb-4">
              Apps we implement
            </h2>
            <p className="font-['Inter'] text-lg max-w-2xl mx-auto" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
              Whichever combination your business needs — sales, finance, HR, support or marketing — we make it work as one system.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {zohoApps.map((app, i) => (
              <Card key={i} className="bg-white transition-all duration-300 hover:shadow-lg hover:-translate-y-1" style={{ border: '1px solid rgba(5, 46, 108, 0.12)' }} data-testid={`zoho-app-card-${i}`}>
                <CardContent className="p-6 flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#11AAB6] to-[#052E6C] flex items-center justify-center flex-shrink-0">
                    <app.icon className="text-white" size={20} />
                  </div>
                  <div>
                    <h3 className="font-['Space_Grotesk'] font-bold text-lg text-[#052E6C] mb-1">{app.name}</h3>
                    <p className="font-['Inter'] text-sm leading-relaxed" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>{app.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why ImpacTech */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="font-['Inter'] text-sm font-semibold uppercase tracking-[0.2em] text-[#11AAB6] mb-2">Why ImpacTech</p>
            <h2 className="font-['Space_Grotesk'] font-bold text-4xl md:text-5xl text-[#052E6C] mb-6">
              A Zoho partner that thinks in growth, not licenses
            </h2>
            <p className="font-['Inter'] text-lg leading-relaxed mb-8" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
              Most implementations fail because they focus on software instead of outcomes. As a digital marketing and automation agency, we connect your Zoho stack directly to your lead generation, sales and customer experience.
            </p>
            <ul className="space-y-4">
              {whyUs.map((point, i) => (
                <li key={i} className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: 'rgba(17, 170, 182, 0.12)' }}>
                    <Rocket className="text-[#11AAB6]" size={13} />
                  </div>
                  <span className="font-['Inter'] leading-relaxed" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>{point}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-6">
            {steps.map((step, i) => (
              <Card key={i} className="relative transition-all duration-300 hover:shadow-lg" style={{ border: '1px solid rgba(5, 46, 108, 0.12)' }}>
                <CardContent className="p-6">
                  <span className="font-['Space_Grotesk'] font-bold text-4xl" style={{ color: 'rgba(17, 170, 182, 0.2)' }}>{step.number}</span>
                  <h3 className="font-['Space_Grotesk'] font-bold text-lg text-[#052E6C] mt-3 mb-2">{step.title}</h3>
                  <p className="font-['Inter'] text-sm leading-relaxed" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#052E6C] py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-['Space_Grotesk'] font-bold text-4xl md:text-5xl text-white mb-6">
            Ready to bring Zoho into your business?
          </h2>
          <p className="font-['Inter'] text-lg mb-8 max-w-2xl mx-auto" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Tell us about your processes and we'll show you exactly how Zoho can automate them — with a clear roadmap and transparent pricing.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              asChild
              className="bg-white text-[#052E6C] hover:bg-white/90 px-8 py-6 text-lg rounded-full font-['Inter'] font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
              data-testid="zoho-cta-contact"
            >
              <Link to="/contact">
                Get a Free Zoho Assessment
                <ArrowRight className="ml-2 inline-block group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <style>{`
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </div>
  );
}
